import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCancelSubscription } from "./useCancelSubscription";
import { ReasonStep } from "./ReasonStep";
import { RetentionOfferStep } from "./RetentionOfferStep";
import { ConfirmationStep } from "./ConfirmationStep";
import type { CancelSubscriptionDialogProps } from "./types";

const STEP_TITLES = {
  reason: "cancelSubscription",
  offer: "weHaveAnOffer",
  confirm: "confirmCancellation",
} as const;

export const CancelSubscriptionDialog = ({
  open,
  onOpenChange,
  onComplete,
}: CancelSubscriptionDialogProps) => {
  const { t, isRTL } = useLanguage();
  const {
    step,
    selectedReason,
    setSelectedReason,
    feedback,
    setFeedback,
    isLoading,
    subscriptionInfo,
    hasExistingDiscount,
    handleReasonContinue,
    handleAcceptDiscount,
    handleDeclineOffer,
    handleConfirmCancel,
    reset,
  } = useCancelSubscription(onComplete);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-background/95 backdrop-blur-xl border-border" dir={isRTL ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-start">
            {t.cancellation[STEP_TITLES[step]]}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {step === "reason" && (
            <ReasonStep
              selectedReason={selectedReason}
              onReasonChange={setSelectedReason}
              feedback={feedback}
              onFeedbackChange={setFeedback}
              onContinue={handleReasonContinue}
            />
          )}

          {step === "offer" && selectedReason && (
            <RetentionOfferStep
              reason={selectedReason}
              hasExistingDiscount={hasExistingDiscount}
              isLoading={isLoading}
              onAcceptDiscount={handleAcceptDiscount}
              onDeclineOffer={handleDeclineOffer}
              onKeepSubscription={() => handleOpenChange(false)}
            />
          )}

          {step === "confirm" && (
            <ConfirmationStep
              isLoading={isLoading}
              subscriptionInfo={subscriptionInfo}
              onConfirmCancel={handleConfirmCancel}
              onKeepSubscription={() => handleOpenChange(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
